// Calm error envelope for the API client.
//
// Every non-2xx HTTP response is normalised here into a typed
// `{ code, message, recovery }` triple. Network failures (TypeError
// from `fetch`) become a synthetic `NETWORK` code with the calm copy
// from README §9. The thrown `ApiError` carries the triple so screens
// can render the message and the recovery side-by-side without
// inspecting status codes.

export interface ApiErrorBody {
  code: string;
  message: string;
  recovery: string;
}

export class ApiError extends Error {
  readonly code: string;
  readonly recovery: string;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.recovery = body.recovery;
  }

  toBody(): ApiErrorBody {
    return { code: this.code, message: this.message, recovery: this.recovery };
  }
}

export function networkError(): ApiErrorBody {
  return {
    code: 'NETWORK',
    message: "Couldn't reach the cooking service — check your connection.",
    recovery: 'Try again in a moment.',
  };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export async function fromBadResponse(response: Response): Promise<ApiErrorBody> {
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    // Server returned a non-JSON body — fall through to generic.
  }
  if (
    isObject(parsed) &&
    typeof parsed.code === 'string' &&
    typeof parsed.message === 'string' &&
    typeof parsed.recovery === 'string'
  ) {
    return {
      code: parsed.code,
      message: parsed.message,
      recovery: parsed.recovery,
    };
  }
  return {
    code: 'BAD_REQUEST',
    message: 'The cooking service returned a response CookChow could not read.',
    recovery: 'Try again in a moment.',
  };
}
